// LE DUEL DIFFÉRÉ côté client (25/07, favorite 5 de l'audit de rétention).
//
// Une section dans le panneau Quêtes : les défis reçus (à relever), et un formulaire pour en lancer
// un. Le choix des cartes ne propose QUE celles où l'on a soi-même un temps, puisque défier c'est
// dire « bats mon temps » : impossible, par construction, de défier sur une carte qu'on n'a jamais
// finie (le serveur le refuserait de toute façon).
//
// Aucune intégration au canvas : tout est DOM, comme le reste des panneaux. Le journal de l'accueil
// annonce les défis reçus, ce panneau sert à les lire et à en envoyer.
import type { DuelDto, ScoreRow } from '../shared/api-types'
import { apiCreateDuel, apiDuels, apiLeaderboard } from './api'
import { formatTime } from './format-time'
import { dashPref, torchPref } from './prefs'
import { showToast } from './economy-ui'

/** Une carte proposable au défi : la carte, mon temps, et les réglages de CE temps. */
export interface CarteDefiable { slug: string; title: string; ms: number; dash: boolean; torch: boolean }

/**
 * Les cartes sur lesquelles je peux défier quelqu'un : TOUTES celles où j'ai un temps,
 * quel que soit le réglage avec lequel je l'ai fait. Le défi partira avec le réglage de
 * CE temps-là, donc l'adversaire jouera dans les mêmes conditions et les deux temps
 * resteront comparables — ce qui était la raison d'être du filtre d'origine.
 *
 * ⚠️ 29/07, signalé par Claude-Alain (« je ne vois pas la map souhaitée »). La liste ne
 * retenait que le réglage COURANT et se vidait EN SILENCE : sur son compte, 18 cartes
 * finies mais 9, 10 ou 17 proposées selon l'état de deux cases à cocher, et six cartes
 * — dont la dernière créée le matin même — invisibles sans le moindre message.
 *
 * UNE seule entrée par carte : le temps du réglage courant s'il existe (le comportement
 * d'avant, celui auquel on s'attend), sinon le meilleur des autres réglages.
 */
export function cartesDefiables(
  levels: readonly { slug: string; title: string; scores: readonly ScoreRow[] }[],
  mePseudo: string, dash: boolean, torch: boolean,
): CarteDefiable[] {
  return levels.flatMap((l) => {
    const miens = l.scores.filter((s) => s.pseudo === mePseudo)
    if (miens.length === 0) return []
    const exact = miens.find((s) => s.dash === dash && s.torch === torch)
    const s = exact ?? miens.reduce((a, b) => (b.bestMs < a.bestMs ? b : a))
    return [{ slug: l.slug, title: l.title, ms: s.bestMs, dash: s.dash, torch: s.torch }]
  })
}

/** Le réglage d'un temps, en clair. Un duel compare deux temps : il faut donc dire
 *  DANS QUELLES CONDITIONS il a été fait, sinon « bats mon temps » ne veut rien dire. */
function modeTexte(dash: boolean, torch: boolean): string {
  return `${dash ? 'avec dash' : 'sans dash'}${torch ? ', torche allumée' : ''}`
}

/** Une ligne « X te défie sur Y : fais mieux que Z ». */
function duelRow(d: DuelDto): HTMLElement {
  const row = document.createElement('div')
  row.className = 'hq-row'
  const top = document.createElement('div')
  top.className = 'hq-row-top'
  const label = document.createElement('span')
  label.className = 'hq-label'
  label.textContent = `${d.other} te défie : ${d.title}`
  const gain = document.createElement('span')
  gain.className = 'hq-gain'
  gain.textContent = `⏱ ${formatTime(d.targetMs)}`
  top.append(label, gain)
  const prog = document.createElement('div')
  prog.className = 'hq-progress'
  prog.textContent = `À relever : joue la carte ${modeTexte(d.dash, d.torch)}, et fais mieux.`
  row.append(top, prog)
  return row
}

/** Un `<select>` simple, étiqueté, aux couleurs du panneau. */
function select(labelText: string, options: { value: string; text: string }[]): { wrap: HTMLElement; el: HTMLSelectElement } {
  const wrap = document.createElement('label')
  wrap.style.cssText = 'display:block;margin:8px 0;font-size:12px;color:#a49bd0'
  wrap.append(labelText)
  const el = document.createElement('select')
  el.style.cssText = 'display:block;width:100%;margin-top:4px;padding:8px;border-radius:8px;border:1px solid #35305e;background:#0d0b1f;color:#f0e9d2;font-family:inherit;font-size:14px'
  for (const o of options) {
    const opt = document.createElement('option')
    opt.value = o.value
    opt.textContent = o.text
    el.appendChild(opt)
  }
  wrap.appendChild(el)
  return { wrap, el }
}

/**
 * Monte la section « Duels » dans le corps du panneau Quêtes. Best-effort : toute erreur réseau
 * laisse simplement la section absente (le panneau Quêtes reste entièrement fonctionnel).
 */
export function mountDuelSection(body: HTMLElement, mePseudo: string): void {
  void (async () => {
    let data
    try {
      data = await apiDuels()
    } catch {
      return // hors ligne : pas de section duels, pas de message d'erreur
    }
    if (!document.body.contains(body)) return // panneau fermé pendant la requête

    const sec = document.createElement('div')
    sec.className = 'hq-section'
    const t = document.createElement('p')
    t.className = 'hq-section-title'
    t.textContent = 'Duels'
    sec.appendChild(t)

    for (const d of data.received) sec.appendChild(duelRow(d))
    if (data.received.length === 0) {
      const vide = document.createElement('div')
      vide.className = 'hq-progress'
      vide.textContent = 'Aucun défi en attente.'
      sec.appendChild(vide)
    }

    // --- Lancer un défi : bouton, puis formulaire dépliant (on ne charge la liste des cartes que
    //     si le joueur clique — inutile de tirer le classement à chaque ouverture du panneau).
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.textContent = '⚔️ Lancer un défi'
    btn.style.cssText = 'display:block;width:100%;margin-top:10px;padding:9px;border-radius:9px;border:0;background:#ffd23f;color:#221b04;font-weight:700;font-size:14px;cursor:pointer'
    sec.appendChild(btn)
    body.insertBefore(sec, body.children[1] ?? null) // juste sous le solde de pièces

    btn.addEventListener('click', () => {
      btn.disabled = true
      btn.textContent = 'Chargement…'
      void (async () => {
        const dash = dashPref(), torch = torchPref()
        let mines: CarteDefiable[] = []
        try {
          mines = cartesDefiables((await apiLeaderboard()).levels, mePseudo, dash, torch)
        } catch {
          showToast('Impossible de charger tes temps pour le moment.')
          btn.disabled = false; btn.textContent = '⚔️ Lancer un défi'
          return
        }
        if (mines.length === 0) {
          showToast('Finis d’abord une carte : un défi, c’est « bats mon temps ».')
          btn.disabled = false; btn.textContent = '⚔️ Lancer un défi'
          return
        }
        btn.remove()
        const qui = select('Qui défier ?', data.family.map((p) => ({ value: p, text: p })))
        // La valeur porte le réglage EN PRÉFIXE (« 1|0|fam-33 ») : un slug peut contenir des
        // tirets, jamais une barre, et découper en tête est sûr quelle que soit sa forme.
        // Le réglage n'est écrit dans le libellé QUE s'il diffère du courant : sinon c'est
        // du bruit sur chaque ligne.
        const quoi = select('Sur quelle carte ?', mines.map((m) => ({
          value: `${m.dash ? 1 : 0}|${m.torch ? 1 : 0}|${m.slug}`,
          text: `${m.title} — ton temps ${formatTime(m.ms)}`
            + (m.dash !== dash || m.torch !== torch ? ` (${modeTexte(m.dash, m.torch)})` : ''),
        })))
        const envoyer = document.createElement('button')
        envoyer.type = 'button'
        envoyer.textContent = 'Envoyer le défi'
        envoyer.style.cssText = 'display:block;width:100%;margin-top:8px;padding:9px;border-radius:9px;border:0;background:#ffd23f;color:#221b04;font-weight:700;font-size:14px;cursor:pointer'
        sec.append(qui.wrap, quoi.wrap, envoyer)
        envoyer.addEventListener('click', () => {
          envoyer.disabled = true
          // on renvoie le réglage DU TEMPS choisi, pas celui des préférences du moment
          const v = quoi.el.value
          const dDash = v[0] === '1', dTorch = v[2] === '1', dSlug = v.slice(4)
          apiCreateDuel(qui.el.value, dSlug, dDash, dTorch)
            .then(() => showToast(`⚔️ Défi envoyé à ${qui.el.value} !`))
            .catch((e: Error) => { showToast(e.message); envoyer.disabled = false })
        })
      })()
    })
  })()
}
